import React, { useEffect, useMemo, useState } from 'react';
import { Building2, ChevronRight, Search, X } from 'lucide-react';
import type {
  MarketingActivityLevel,
  MarketingCompetitorDetail,
  MarketingReport,
  MarketingReportListItem,
} from '../../types';
import {
  subscribeMarketingReport,
  subscribeMarketingReports,
} from '../../utils/marketingReportsService';
import { fmtReportDateLabel, localReportDateKey } from '../../utils/marketingReportDates';
import MarketingReportDateBar from './MarketingReportDateBar';

type CompetitorFilter = 'all' | 'updates';

function activityLabel(level: MarketingActivityLevel): string {
  switch (level) {
    case 'low':
      return 'Low activity';
    case 'high':
      return 'High activity';
    default:
      return 'Medium activity';
  }
}

function formatKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatValue(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value.map(v => (typeof v === 'string' ? v : JSON.stringify(v))).join(', ');
  }
  return JSON.stringify(value, null, 2);
}

function competitorSummary(detail: MarketingCompetitorDetail): string | null {
  for (const key of [
    'summary',
    'update',
    'updates',
    'headline',
    'note',
    'notes',
    'highlight',
    'change',
    'changes',
    'activity',
    'description',
  ]) {
    const raw = detail[key];
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
  }
  return null;
}

function competitorTeaser(detail: MarketingCompetitorDetail): string {
  const summary = competitorSummary(detail);
  if (summary) return summary;
  for (const [key, raw] of Object.entries(detail)) {
    if (key === 'name' || key === 'has_updates') continue;
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
  }
  return detail.has_updates ? 'Has updates' : 'No updates this period';
}

function competitorExtraFields(detail: MarketingCompetitorDetail): Array<{ key: string; value: string }> {
  const skip = new Set(['name', 'has_updates', 'summary', 'update', 'updates', 'headline', 'note', 'notes']);
  const out: Array<{ key: string; value: string }> = [];
  for (const [key, raw] of Object.entries(detail)) {
    if (skip.has(key)) continue;
    const value = formatValue(raw);
    if (!value || value === '—' || value === '{}' || value === '[]') continue;
    out.push({ key, value });
  }
  return out;
}

function sortCompetitors(details: MarketingCompetitorDetail[]): MarketingCompetitorDetail[] {
  return [...details].sort((a, b) => {
    if (a.has_updates !== b.has_updates) return a.has_updates ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}

function KvPanel({ title, data }: { title: string; data?: Record<string, unknown> }) {
  if (!data || Object.keys(data).length === 0) return null;
  return (
    <section className="mh-brief-panel">
      <h4>{title}</h4>
      <dl className="mh-radar-fields">
        {Object.entries(data).map(([key, value]) => (
          <div key={key} className="mh-radar-field">
            <dt>{formatKey(key)}</dt>
            <dd>{formatValue(value)}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function RecordPanel({ title, items }: { title: string; items?: Record<string, unknown>[] }) {
  if (!items?.length) return null;
  return (
    <section className="mh-brief-panel">
      <h4>
        {title}
        <span className="mh-brief-panel-count">{items.length}</span>
      </h4>
      <div className="mh-brief-panel-list">
        {items.map((item, i) => (
          <dl key={i} className="mh-brief-panel-item mh-radar-fields">
            {Object.entries(item).map(([key, value]) => (
              <div key={key} className="mh-radar-field">
                <dt>{formatKey(key)}</dt>
                <dd>{formatValue(value)}</dd>
              </div>
            ))}
          </dl>
        ))}
      </div>
    </section>
  );
}

function CompetitorDetailPanel({
  detail,
  onClose,
}: {
  detail: MarketingCompetitorDetail | null;
  onClose?: () => void;
}) {
  if (!detail) {
    return (
      <aside className="mh-brief-detail">
        <div className="mh-brief-detail-empty">Select a competitor</div>
      </aside>
    );
  }

  const summary = competitorSummary(detail);
  const extras = competitorExtraFields(detail);

  return (
    <aside className="mh-brief-detail">
      <header className="mh-brief-detail-header">
        <div>
          <div className="mh-brief-detail-top">
            <h3>{detail.name}</h3>
            {detail.has_updates ? (
              <span className="mh-brief-badge mh-brief-badge--update">Update</span>
            ) : (
              <span className="mh-brief-badge">Quiet</span>
            )}
          </div>
        </div>
        {onClose && (
          <button type="button" className="mh-brief-detail-close" onClick={onClose} aria-label="Close details">
            <X size={14} />
          </button>
        )}
      </header>
      <div className="mh-brief-detail-body">
        {summary && <p className="mh-brief-detail-summary">{summary}</p>}
        {extras.length > 0 ? (
          <dl className="mh-radar-fields">
            {extras.map(field => (
              <div key={field.key} className="mh-radar-field">
                <dt>{formatKey(field.key)}</dt>
                <dd>{field.value}</dd>
              </div>
            ))}
          </dl>
        ) : (
          !summary && <p className="mh-brief-detail-muted">No additional fields for this competitor.</p>
        )}
      </div>
    </aside>
  );
}

const MarketingCompetitorBriefingPage: React.FC = () => {
  const [listLoading, setListLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [reports, setReports] = useState<MarketingReportListItem[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [report, setReport] = useState<MarketingReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<CompetitorFilter>('all');
  const [query, setQuery] = useState('');
  const [selectedName, setSelectedName] = useState<string | null>(null);

  const todayKey = useMemo(() => localReportDateKey(), []);

  useEffect(() => {
    const unsub = subscribeMarketingReports(
      rows => {
        setReports(rows);
        setListLoading(false);
        setSelectedDate(prev => {
          if (prev && rows.some(r => r.report_date === prev)) return prev;
          const today = rows.find(r => r.report_date === todayKey);
          return today?.report_date ?? rows[0]?.report_date ?? null;
        });
      },
      err => {
        setError(err.message);
        setListLoading(false);
      },
    );
    return unsub;
  }, [todayKey]);

  useEffect(() => {
    if (!selectedDate) {
      setReport(null);
      setReportLoading(false);
      return;
    }
    setReportLoading(true);
    setSelectedName(null);
    const unsub = subscribeMarketingReport(
      selectedDate,
      data => {
        setReport(data);
        setReportLoading(false);
      },
      err => {
        setError(err.message);
        setReportLoading(false);
      },
    );
    return unsub;
  }, [selectedDate]);

  const allCompetitors = useMemo(
    () => sortCompetitors(report?.competitor_details ?? []),
    [report],
  );
  const updateCount = useMemo(
    () => allCompetitors.filter(d => d.has_updates).length,
    [allCompetitors],
  );

  const visibleCompetitors = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allCompetitors.filter(d => {
      if (filter === 'updates' && !d.has_updates) return false;
      if (!q) return true;
      const hay = [
        d.name,
        competitorSummary(d) ?? '',
        ...competitorExtraFields(d).map(f => `${f.key} ${f.value}`),
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [allCompetitors, filter, query]);

  useEffect(() => {
    if (!visibleCompetitors.length) {
      setSelectedName(null);
      return;
    }
    setSelectedName(prev => {
      if (prev && visibleCompetitors.some(d => d.name === prev)) return prev;
      const firstUpdate = visibleCompetitors.find(d => d.has_updates);
      return (firstUpdate ?? visibleCompetitors[0]).name;
    });
  }, [visibleCompetitors]);

  const selectedCompetitor = useMemo(
    () => visibleCompetitors.find(d => d.name === selectedName) ?? null,
    [visibleCompetitors, selectedName],
  );

  const marketScanHasContent = Boolean(
    report &&
      (report.seo_keywords_analysis ||
        report.google_business_profile_monitoring ||
        report.marketing_tone_style_feel ||
        (report.new_kits_released && report.new_kits_released.length > 0) ||
        (report.partnerships_observed && report.partnerships_observed.length > 0) ||
        report.key_market_trends ||
        report.daily_highlights.length > 0),
  );

  const showWhatChanged = Boolean(
    report?.key_market_trends &&
      report.key_market_trends.trim() &&
      report.key_market_trends.trim() !== (report.daily_highlights[0] || '').trim(),
  );

  if (listLoading) {
    return (
      <div className="marketing-hub-loading">
        <div className="marketing-hub-loading-bar" />
      </div>
    );
  }

  if (!reports.length) {
    return (
      <div className="marketing-hub-layout marketing-hub-layout--subpage">
        <div className="marketing-hub-main">
          <div className="marketing-hub-toolbar">
            <div className="marketing-hub-toolbar-left">
              <h2 className="marketing-hub-title">Competitor Briefing</h2>
            </div>
          </div>
          <div className="mh-radar-empty">
            <Building2 size={28} strokeWidth={1.5} />
            <h3>No briefings yet</h3>
            <p>The marketing agent publishes a daily competitor scan. Check back after the next run.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="marketing-hub-layout marketing-hub-layout--subpage">
      <div className="marketing-hub-main">
        <div className="marketing-hub-toolbar">
          <div className="marketing-hub-toolbar-left">
            <h2 className="marketing-hub-title">Competitor Briefing</h2>
            <div className="marketing-hub-stats">
              <span className="mh-stat">{allCompetitors.length} competitors</span>
              {updateCount > 0 && (
                <span className="mh-stat mh-stat-published">{updateCount} with updates</span>
              )}
              {report && (
                <span className={`mh-stat mh-stat-activity-${report.overall_activity_level}`}>
                  {activityLabel(report.overall_activity_level)}
                </span>
              )}
            </div>
          </div>
          <div className="marketing-hub-toolbar-right">
            <div className="mh-brief-controls">
              <div className="mh-brief-filters" role="tablist" aria-label="Competitor filter">
                <button
                  type="button"
                  className={`mh-brief-filter${filter === 'all' ? ' active' : ''}`}
                  onClick={() => setFilter('all')}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`mh-brief-filter${filter === 'updates' ? ' active' : ''}`}
                  onClick={() => setFilter('updates')}
                >
                  Updates
                </button>
              </div>
              <label className="mh-brief-search">
                <Search size={13} aria-hidden />
                <input
                  type="search"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Search"
                  aria-label="Search competitors"
                />
              </label>
            </div>
            <MarketingReportDateBar
              reports={reports.map(r => ({
                id: r.report_date,
                report_date: r.report_date,
                headline: r.headline,
              }))}
              selectedId={selectedDate}
              onSelectId={setSelectedDate}
              label="Report"
              compact
              todayKey={todayKey}
              todaySelectId={todayKey}
              onJumpToToday={() => {
                if (reports.some(r => r.report_date === todayKey)) {
                  setSelectedDate(todayKey);
                }
              }}
            />
          </div>
        </div>

        <div className="mh-brief-workspace">
          {error && (
            <div className="mh-requests-error" role="alert">
              {error}
            </div>
          )}

          {reportLoading && !report ? (
            <div className="marketing-hub-loading">
              <div className="marketing-hub-loading-bar" />
            </div>
          ) : report ? (
            <>
              <div className="mh-brief-split">
                <div className="mh-brief-table-wrap">
                  <div className="mh-brief-table-head" aria-hidden>
                    <span>Status</span>
                    <span>Competitor</span>
                    <span>Update</span>
                    <span />
                  </div>
                  {visibleCompetitors.length > 0 ? (
                    <div className="mh-brief-table" role="listbox" aria-label="Competitors">
                      {visibleCompetitors.map(d => {
                        const selected = d.name === selectedName;
                        return (
                          <button
                            key={d.name}
                            type="button"
                            role="option"
                            aria-selected={selected}
                            className={`mh-brief-tr${d.has_updates ? ' mh-brief-tr--update' : ''}${
                              selected ? ' selected' : ''
                            }`}
                            onClick={() => setSelectedName(d.name)}
                          >
                            <span className="mh-brief-tr-status">
                              {d.has_updates ? (
                                <span className="mh-brief-badge mh-brief-badge--update">Update</span>
                              ) : (
                                <span className="mh-brief-badge">Quiet</span>
                              )}
                            </span>
                            <span className="mh-brief-tr-name">{d.name}</span>
                            <span className="mh-brief-tr-teaser">{competitorTeaser(d)}</span>
                            <ChevronRight size={14} className="mh-brief-tr-chevron" aria-hidden />
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="mh-brief-empty-list">
                      No competitors match {query.trim() ? 'your search' : 'this filter'}.
                    </div>
                  )}
                </div>

                <div className={`mh-brief-detail-wrap${selectedCompetitor ? ' open' : ''}`}>
                  <CompetitorDetailPanel
                    detail={selectedCompetitor}
                    onClose={() => setSelectedName(null)}
                  />
                </div>
              </div>

              {marketScanHasContent && (
                <details className="mh-brief-scan">
                  <summary>Market scan</summary>
                  <div className="mh-brief-scan-body">
                    {report.daily_highlights.length > 0 && (
                      <section className="mh-brief-panel">
                        <h4>Highlights</h4>
                        <ul className="mh-brief-bullets">
                          {report.daily_highlights.map((h, i) => (
                            <li key={i}>{h}</li>
                          ))}
                        </ul>
                      </section>
                    )}
                    <KvPanel title="SEO themes" data={report.seo_keywords_analysis} />
                    <KvPanel title="GBP watch" data={report.google_business_profile_monitoring} />
                    <KvPanel title="Tone & style" data={report.marketing_tone_style_feel} />
                    <RecordPanel title="New kits" items={report.new_kits_released} />
                    <RecordPanel title="Partnerships" items={report.partnerships_observed} />
                    {showWhatChanged && report.key_market_trends && (
                      <section className="mh-brief-panel">
                        <h4>What changed</h4>
                        <p className="mh-brief-panel-body">{report.key_market_trends}</p>
                      </section>
                    )}
                    {(report.focus_competitors.length > 0 || report.data_notes) && (
                      <footer className="mh-brief-footer">
                        {report.focus_competitors.length > 0 && (
                          <div className="mh-brief-chips">
                            <span className="mh-brief-chips-label">Focus</span>
                            {report.focus_competitors.map(c => (
                              <span key={c} className="mh-brief-chip">
                                {c}
                              </span>
                            ))}
                          </div>
                        )}
                        {report.data_notes && <p className="mh-brief-notes">{report.data_notes}</p>}
                      </footer>
                    )}
                  </div>
                </details>
              )}
            </>
          ) : (
            <div className="mh-radar-empty">
              <p>No briefing found for {selectedDate ? fmtReportDateLabel(selectedDate) : 'this date'}.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketingCompetitorBriefingPage;
